import { Component, Input, OnChanges } from '@angular/core';
import { Store, Select } from '@ngxs/store';
import { UpdateDealDetails } from 'app/store/deal/deal/deal.actions';
import { Application } from 'app/models/dto/deal-application.model';
import { SalesRepOptions } from 'app/models/dto/sales-rep-options';
import { DealState } from 'app/store/deal/deal/deal.state';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormlyDialogComponent } from 'ng2-app/components/formly-dialog/formly-dialog.component';
import { UntypedFormGroup } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { take, map } from 'rxjs/operators';
import { UserDirectoryState } from 'app/store/user-directory/user-directory.state';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';
import { LenderUser } from 'app/models/dto/lender-user.model';
import { format } from 'date-fns';

@Component({
  selector: 'app-deal-timeline-smart',
  templateUrl: './deal-timeline-smart.component.html',
  styleUrls: ['./deal-timeline-smart.component.scss'],
})
export class DealSmartTimelineComponent implements OnChanges {
  @Input() dealCreateTimeStamp: string;
  @Input() dealApplications: Application[];
  @Input() closerName: string;
  @Input() closerId: number;
  @Input() dealId: number;
  @Select(UserDirectoryState.users) userDirectory$: Observable<LenderUser[]>;
  @Select(LenderUserState.lenderUser) lenderUser$: Observable<LenderUser>;


  dealCreateDt: string;
  dealCreateTime: string;
  dealClosedDt: string;
  dealClosedTime: string;
  dealCloserOptions: SalesRepOptions[] = [];
  editing = false;
  dealDateClosed: string | number | Date | undefined;
  userCanEditFundedDetails: boolean;
  dealCreatedFormattedTimeStamp: Date | boolean;
  private dialogRef: MatDialogRef<FormlyDialogComponent>;

  constructor(
    private store: Store,
    private dialog: MatDialog,
  ) { }


  ngOnChanges(): void {
    const date: Date = new Date(this.dealCreateTimeStamp);
    this.dealCreatedFormattedTimeStamp = isNaN(date.getTime()) ? false : date;
    this.lenderUser$.pipe(take(1)).subscribe((user) => {
      this.userCanEditFundedDetails = user.permissions.includes('deal.write');
    });
    this.store.select(DealState.dealDateClosed).pipe(map(x => x(this.dealId)))
      .subscribe(dealDateClosed => {
        this.dealDateClosed = dealDateClosed;
    });
  }

  handleEditClosedBy() {
    this.editing = true;
    this.openDialog();
  }

  sortBy() {
    return this.dealApplications.sort((a, b) => {
      if (a.createdTimestamp > b.createdTimestamp) {
        return 1;
      } else if (a.createdTimestamp < b.createdTimestamp) {
        return -1;
      } else {
        return 0;
      }
    });
  }

  trackByAppId(index: number, application: Application): number {
    return application.id;
  }

  loadReps() {
    this.userDirectory$.subscribe(users => {
      if (!(users.length > 0)) {
        return;
      }
      const lenderUsers = users.map(user => ({
          label: user.fullName,
          value: user.id
        }));
      lenderUsers.sort((a, b) => {
        const first = a.label.toUpperCase();
        const second = b.label.toUpperCase();
        if (first < second) {
          return -1;
        } else if (first > second) {
          return 1;
        }
        return 0;
      });
      this.dealCloserOptions = lenderUsers;
      this.editing = false;

      if (this.dialogRef && this.dialogRef.componentInstance) {
        this.dialogRef.componentInstance.data.loadingInitData$ = of(false);
        const fields = this.dialogRef.componentInstance.data.formlyConfig.fields[0];
        if (fields && fields.templateOptions && fields.templateOptions.options) {
          fields.templateOptions.options = lenderUsers;
        }
      }
    });
  }

  openDialog() {
    this.loadReps();
    const fields = [
      {
        key: 'dealCloser',
        type: 'select',
        templateOptions: {
          label: 'Select Rep Name',
          required: true,
          options: this.dealCloserOptions
        }
      }, {
        key: 'dateClosed',
        type: 'datepicker',
        templateOptions: {
          label: 'Choose a Date'
        }
      }
    ];
    this.dialogRef = this.dialog.open(FormlyDialogComponent, {
      data: {
        loading$: false,
        loadingInitData$: of(this.dealCloserOptions.length === 0),
        title: 'Edit Deal Closed By',
        formlyConfig: {
          model: { dealCloser: this.closerId, dateClosed: this.dealDateClosed ?  new Date(this.dealDateClosed) : null },
          form: new UntypedFormGroup({}),
          fields: fields
        }
      },
      width: '428px',
      autoFocus: false,
      panelClass: 'closedBy-dialog-container',
    });

    const lenderUserfields = this.dialogRef.componentInstance.data.formlyConfig.fields[0];
    if (lenderUserfields && lenderUserfields.templateOptions && lenderUserfields.templateOptions.options) {
      lenderUserfields.templateOptions.options = this.dealCloserOptions;
    }

    this.dialogRef.componentInstance.cancel.subscribe(() => {
      this.editing = false;
    });

    this.dialogRef.componentInstance.saveData.subscribe(changedData => {
      this.dialogRef.componentInstance.data.loading$ = of(true);
      this.updateCloser(changedData.formlyConfig.model.dealCloser, changedData.formlyConfig.model.dateClosed);
    },
      error => {
        this.dialogRef.componentInstance.data.loading$ = of(false);
        if (error.message) {
          this.dialogRef.componentInstance.data.errorMessage = error.message;
        } else {
          this.dialogRef.componentInstance.data.errorMessage = 'Failed to update closed by value';
        }
      }
    );
  }

  updateCloser(closedBy: number, dt: string) {
    const closer = this.dealCloserOptions.find(user => user.value === closedBy);
    const dateClosed = format(new Date(dt), 'yyyy-MM-dd hh:mm:ss');
    if (!closer) {
      return;
    }
    this.store.dispatch(new UpdateDealDetails(this.dealId,
        { closedBy, dateClosed})).pipe(take(1)).subscribe(() => {
      this.closerName = closer.label;
      this.closerId = closer.value;
      this.dialogRef.close();
    });
  }
}
